import { CanvasCalendarBody } from "@/components/calendars/CanvasCalendarBody.tsx";
import { type ComponentProps, useMemo } from "react";
import { CanvasLayers } from "./CanvasLayers.tsx";
import { parseCanvasContent } from "./canvasContent.ts";
import { useSource } from "./useSource.ts";

/** Stable empty stack, so an absent `canvasStack` prop keeps the same reference
 *  across renders and doesn't recompute the root memo every render. */
const EMPTY_STACK: string[] = [];

type CanvasCalendarBodyProps = ComponentProps<typeof CanvasCalendarBody>;

export type CanvasHostProps = Omit<
  CanvasCalendarBodyProps,
  "calendarEditable" | "highlightedEvent"
> & {
  /** The canvas modal stack (empty = resting calendar). A plain render input —
   *  the hosting surface reads the canvas state and passes it in. */
  canvasStack?: string[];
  /** Whether the pane is expanded — gates focus restoration into a revealed card. */
  isCanvasOpen?: boolean;
  /** Sticky display mode of the presented entity, forwarded to each card. A plain
   *  render input, like `canvasStack`. */
  isFullscreen?: boolean;
  /** Whether the base calendar allows drag-to-reschedule. Required so every
   *  surface chooses explicitly — a silent default would leave a new surface
   *  read-only with no visible signal. */
  calendarEditable: boolean;
};

/**
 * The canvas surface: an always-mounted calendar base with the modal stack
 * ({@link CanvasLayers}) layered on top of it. The calendar never unmounts as
 * layers come and go.
 *
 * A root `source` that resolves to an `event` also focuses the base calendar —
 * jump-to-day plus a ring on its chip, owned by {@link CanvasCalendarBody} — so
 * the calendar behind the event's card is showing that event, and closing the
 * card lands the user on it. Deliberately not spotlit: the card's own scrim
 * already dims the calendar, and a second dim underneath it reads as a bug.
 *
 * The stack comes in as the `canvasStack` prop; actions (`pop` / `clear`) go
 * through the same context external callers use to drive the canvas.
 */
export const CanvasHost = ({
  canvasStack = EMPTY_STACK,
  isCanvasOpen = true,
  isFullscreen = false,
  calendarEditable,
  ...calendarProps
}: CanvasHostProps) => {
  // Only the root decides the calendar's highlight, so this parses that one
  // token rather than the whole stack `CanvasLayers` walks.
  const rootLayer = useMemo(
    () => (canvasStack[0] ? parseCanvasContent(canvasStack[0]) : null),
    [canvasStack],
  );

  // Focus the root event on the base calendar. CanvasSourceModal resolves the
  // same id independently for its card; React Query dedupes the fetch.
  const { data: rootSource } = useSource(rootLayer?.kind === "source" ? rootLayer.id : undefined);
  const highlightedEvent = rootSource?.type === "event" ? rootSource.detail : undefined;

  return (
    // Deliberately unpositioned: the stack's layer wrappers are `absolute
    // inset-0`, so leaving this static lets them resolve against
    // `SplitPanelFrame` and cover the calendar header as well as the body. Add
    // `relative` here and the scrim stops at the header's bottom edge, leaving
    // the month title, view switcher, and account switcher undimmed above an
    // open card. `CanvasCalendarBody` scopes its own grid overlays, so nothing
    // inside depends on this element being the containing block.
    //
    // Cards centre and cap their height against that same wrapper, so they sit
    // one header lower and can grow one header taller than they would against
    // the body alone. That is intended — the card centres on the region it
    // dims — and not a side effect to be corrected.
    //
    // A `full` card takes the whole wrapper for the same reason: it covers the
    // region its scrim dims, header included. The header's controls — including
    // the account switcher, which lives there whenever the canvas is visible —
    // are unreachable until the card is closed or returned to `card`.
    <div className="flex flex-1 min-h-0 flex-col">
      <CanvasCalendarBody
        calendarEditable={calendarEditable}
        {...calendarProps}
        highlightedEvent={highlightedEvent}
      />
      <CanvasLayers
        canvasStack={canvasStack}
        isCanvasOpen={isCanvasOpen}
        isFullscreen={isFullscreen}
      />
    </div>
  );
};
